import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import strings from '../i18n/language';
import {
  resetUserAction,
  definePasswordAction,
  cleanErrorAction,
  resetUserPasswordAction,
} from '../redux/actions/user';

// Same look as the login page
const BRAND = '#3b95e8';
const MUTED = 'rgba(232,240,254,0.45)';
const BORDER = 'rgba(255,255,255,0.10)';
const ERROR = '#ff7b7b';

const inputStyle = {
  width: '100%',
  padding: '14px 16px',
  background: 'rgba(255,255,255,0.06)',
  border: `1px solid ${BORDER}`,
  borderRadius: 12,
  color: '#e8f0fe',
  fontSize: 15,
  fontFamily: 'system-ui, -apple-system, sans-serif',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s',
};

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600, color: MUTED, marginBottom: 6, letterSpacing: 0.4,
};

const Field = ({ label, hint, invalid, focused, onFocus, onBlur, ...inputProps }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={labelStyle}>{label}</label>
    <input
      {...inputProps}
      onFocus={onFocus}
      onBlur={onBlur}
      style={{
        ...inputStyle,
        borderColor: invalid ? ERROR : focused ? BRAND : BORDER,
      }}
    />
    {hint && (
      <div style={{ fontSize: 12, lineHeight: 1.5, color: invalid ? ERROR : MUTED, marginTop: 6 }}>
        {hint}
      </div>
    )}
  </div>
);

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [honeyPot, setHoneyPot] = useState('');
  const [email, setEmail] = useState('');
  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const { token } = useParams();
  const [errorMail, setErrorMail] = useState(false);
  const [errorPassword1, setErrorPassword1] = useState(false);
  const [errorPassword2, setErrorPassword2] = useState(false);
  const resetPassword = useSelector((state) => state.user.resetPassword);
  const definePassword = useSelector((state) => state.user.definePassword);
  const isLoadingReset = useSelector((state) => state.user.isLoadingUserReset);
  const isLoadingDefine = useSelector((state) => state.user.isLoadingUserDefine);
  const error = useSelector((state) => state.user.error);

  // The reset request always answers the same, known address or not
  // Any failure is shown, the server unreachable included: only the token
  // errors were, so a failed request looked like a dead button
  useEffect(() => {
    if (error) {
      dispatch(cleanErrorAction());
      toast.error(strings.LABEL_RESET_GLOBAL_ERROR);
    }
  }, [error, dispatch, navigate]);

  useEffect(() => {
    if (resetPassword) {
      setEmail('');
      setErrorMail(false);
      toast.success(strings.LABEL_RESET_PASSWORD_RESET_DONE, {
        onClose: () => {
          dispatch(resetUserAction());
          navigate('/');
        },
      });
    }
  }, [resetPassword, navigate, dispatch]);

  useEffect(() => {
    if (definePassword) {
      setPassword1('');
      setPassword2('');
      setErrorPassword1(false);
      setErrorPassword2(false);
      toast.success(strings.LABEL_RESET_DEFINE_DONE, {
        onClose: () => {
          dispatch(resetUserAction());
          navigate('/');
        },
      });
    }
  }, [definePassword, navigate, dispatch]);

  useEffect(() => {
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*[\W_]).{6,}$/;
    if (password1 && passwordRegex.test(password1)) {setErrorPassword1(false);}
    else {setErrorPassword1(true);}
  }, [password1]);

  useEffect(() => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (email && emailRegex.test(email)) {setErrorMail(false);}
    else {setErrorMail(true);}
  }, [email]);

  // Abuse is limited by the server (per address and per IP), so there is no
  // question to answer here
  const sendResetPassword = (event) => {
    event.preventDefault();
    // A bot gets the usual answer, but nothing is sent
    if (honeyPot) {
      toast.success(strings.LABEL_RESET_PASSWORD_RESET_DONE);
      return null;
    }
    if (!errorMail) {
      dispatch(
        resetUserPasswordAction({
          email: email,
        }),
      );
    } else {toast.error(strings.LABEL_RESET_ERROR_VERIFY);}
  };

  const sendDefinePassword = (event) => {
    event.preventDefault();
    if (!errorPassword1 && !errorPassword2) {
      dispatch(
        definePasswordAction({
          token: token,
          password: password1,
        }),
      );
    } else {toast.error(strings.LABEL_RESET_ERROR_VERIFY);}
  };

  useEffect(() => {
    if (password1 && password2 && password1 === password2)
    {setErrorPassword2(false);}
    else {setErrorPassword2(true);}
  }, [password1, password2]);

  const focusProps = (name) => ({
    focused: focusedField === name,
    onFocus: () => setFocusedField(name),
    onBlur: () => setFocusedField(null),
  });

  const isLoading = token ? isLoadingDefine : isLoadingReset;

  const submitButton = (
    <button
      type="submit"
      disabled={isLoading}
      style={{
        width: '100%',
        padding: '14px',
        marginTop: 8,
        background: BRAND,
        color: '#fff',
        border: 'none',
        borderRadius: 12,
        fontSize: 15,
        fontWeight: 700,
        cursor: isLoading ? 'default' : 'pointer',
        opacity: isLoading ? 0.6 : 1,
        letterSpacing: 0.3,
        boxShadow: '0 6px 24px rgba(59,149,232,0.35)',
      }}>
      {strings.LABEL_BUTTON_RETRIEVE_PASSWORD}
    </button>
  );

  return (
    <>
      <style>{`
        html, body { margin: 0; padding: 0; }
        input::placeholder { color: rgba(232,240,254,0.3); }
        input:-webkit-autofill {
          -webkit-box-shadow: 0 0 0 1000px rgba(30,58,95,0.9) inset !important;
          -webkit-text-fill-color: #e8f0fe !important;
        }
      `}</style>
      <ToastContainer position="bottom-right" />

      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #0a1628 0%, #0f1f3d 50%, #0a1628 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: '24px',
        boxSizing: 'border-box',
        position: 'relative',
      }}>

        {/* Glow */}
        <div style={{
          position: 'absolute', top: '40%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500, height: 500, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59,149,232,0.10) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        {/* Back button */}
        <button
          onClick={() => navigate('/login')}
          style={{
            position: 'fixed', top: 20, left: 20, zIndex: 10,
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'rgba(255,255,255,0.07)',
            color: 'rgba(232,240,254,0.7)',
            border: `1px solid ${BORDER}`,
            borderRadius: 10, padding: '8px 16px',
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
            backdropFilter: 'blur(8px)',
          }}>
          ← Volver
        </button>

        {/* Card */}
        <div style={{
          width: '100%', maxWidth: 400,
          background: 'rgba(255,255,255,0.04)',
          border: `1px solid ${BORDER}`,
          borderRadius: 24,
          padding: '40px 36px',
          boxSizing: 'border-box',
          position: 'relative',
        }}>

          {/* Logo + title */}
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <div style={{
                width: 56, height: 56, borderRadius: 16,
                background: 'linear-gradient(135deg, #3b95e8 0%, #1a6fc4 100%)',
                boxShadow: '0 6px 24px rgba(59,149,232,0.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ fontFamily: 'FingerPaint, serif', fontSize: 30, color: '#fff', lineHeight: 1, marginTop: 2 }}>f</span>
              </div>
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 2, color: BRAND, textTransform: 'uppercase', marginBottom: 14 }}>
              Fidelize
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#fff', letterSpacing: -0.3, marginBottom: 8 }}>
              {token ? strings.LABEL_RESET_DEFINE_PASSWORD_TITLE : strings.LABEL_RESET_PASSWORD_TITLE}
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.6, color: MUTED }}>
              {token ? strings.LABEL_RESET_DEFINE_PASSWORD_CONTENT : strings.LABEL_RESET_PASSWORD_CONTENT}
            </div>
          </div>

          {!token ? (
            <form noValidate id="resetForm" onSubmit={sendResetPassword}>
              <Field
                label={strings.LABEL_INPUT_MAIL}
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                invalid={!!email && errorMail}
                hint={email && errorMail ? strings.LABEL_RESET_PASSWORD_MAIL_ERROR : null}
                {...focusProps('email')}
              />

              {/* Bots fill every field: a person never sees this one.
                  display:none, as browsers autofill a field merely moved
                  off screen */}
              <input
                type="text"
                name="honeypot"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={honeyPot}
                onChange={(e) => setHoneyPot(e.target.value)}
                style={{ display: 'none' }}
              />

              {submitButton}
            </form>
          ) : (
            <form noValidate onSubmit={sendDefinePassword}>
              <Field
                label={strings.LABEL_RESET_PASSWORD_1}
                type="password"
                autoComplete="new-password"
                autoFocus
                value={password1}
                onChange={(e) => setPassword1(e.target.value)}
                invalid={!!password1 && errorPassword1}
                hint={strings.LABEL_RESET_PASSWORD_1_ERROR}
                {...focusProps('password1')}
              />
              <Field
                label={strings.LABEL_RESET_PASSWORD_2}
                type="password"
                autoComplete="new-password"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                invalid={!!password2 && errorPassword2}
                hint={password2 && errorPassword2 ? strings.LABEL_RESET_PASSWORD_2_ERROR : null}
                {...focusProps('password2')}
              />
              {submitButton}
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
